import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Mail, MessageCircle, Send, Clock, TrendingUp } from "lucide-react";
import { IconUpload, IconBrandYoutube } from "@tabler/icons-react";
import { ElementType } from "react";
import { toast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import AppLayout from "@/components/layout/AppLayout";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { config } from "@/lib/config";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useQueryClient } from "@tanstack/react-query";
import { StudyModeSelector, StudyMode } from "@/components/topic/StudyModeSelector";
import TopicTilesGrid from "@/components/cards/TopicTilesGrid";
import { TopicCreationWidget } from "@/components/topic/TopicCreationWidget";
import StreakCard from "@/components/cards/StreakCard";
import OnboardingChecklist from "@/components/cards/OnboardingChecklist";
import SubscriptionCard from "@/components/subscription/SubscriptionCard";
import RecommendedTopics from "@/components/recommendations/RecommendedTopics";
import StudyRemindersSetup from "@/components/analytics/StudyRemindersSetup";
import { useStudyAnalytics } from "@/hooks/useStudyAnalytics";
import { useIsTabletOrMobile } from "@/hooks/use-tablet-mobile";
import { useScrollIndicator } from "@/hooks/useScrollIndicator";
import { useCreateTopic } from "@/hooks/useCreateTopic";
import { YouTubeUrlModal } from "@/components/youtube/YouTubeUrlModal";
import { useDocumentUpload } from "@/hooks/useDocumentUpload";
import { fetchVideoInfo } from "@/lib/youtube-service";
interface Activity {
  id: string;
  title: string;
  icon: ElementType;
  description: string;
}
const activities: Activity[] = [{
  id: 'upload-document',
  title: 'Upload',
  icon: IconUpload,
  description: 'Upload files for AI analysis'
}, {
  id: 'youtube-link',
  title: 'Paste',
  icon: IconBrandYoutube,
  description: 'Learn from YouTube videos'
}];
const LandingPage = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [sessionDescription, setSessionDescription] = useState("");
  const [selectedDocument, setSelectedDocument] = useState("");
  const [documents, setDocuments] = useState<Array<{
    id: string;
    name: string;
    topicName: string | null;
    uploadTimestamp?: string;
  }>>([]);
  const [showYouTubeModal, setShowYouTubeModal] = useState(false);
  const [selectedStudyMode, setSelectedStudyMode] = useState<StudyMode | null>(null);
  const [showTextArea, setShowTextArea] = useState(false);
  const [userProfile, setUserProfile] = useState<{
    full_name?: string | null;
  }>({});
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const isTabletOrMobile = useIsTabletOrMobile();
  const {
    isScrolling,
    handleScroll
  } = useScrollIndicator();
  const {
    user
  } = useAuth();
  const {
    data: analytics
  } = useStudyAnalytics();
  const {
    trackDocumentUpload,
    trackError
  } = useAnalytics();
  const queryClient = useQueryClient();

  // Topic creation hook
  const {
    createTopic,
    isCreating
  } = useCreateTopic();
  const {
    uploadDocument
  } = useDocumentUpload();

  // Check onboarding status for new users
  useEffect(() => {
    const checkUserStatus = async () => {
      if (!user) return;
      try {
        // Check user profile and activity
        const [profileResult, documentsResult, topicsResult, sessionsResult] = await Promise.all([supabase.from('profiles').select('onboarding_completed, full_name').eq('id', user.id).maybeSingle(), supabase.from('documents').select('id').eq('user_id', user.id).limit(1), supabase.from('topics').select('id, title').eq('user_id', user.id), supabase.from('sessions').select('id').eq('user_id', user.id).limit(1)]);
        if (profileResult.error) {
          console.error('Error fetching profile:', profileResult.error);
          return;
        }
        const profile = profileResult.data;

        // Set user profile data
        if (profile) {
          setUserProfile({
            full_name: profile.full_name
          });
        }
        const hasDocuments = documentsResult.data && documentsResult.data.length > 0;
        const hasTopics = topicsResult.data && topicsResult.data.length > 1; // More than just "General"
        const hasSessions = sessionsResult.data && sessionsResult.data.length > 0;
        const hasActivity = hasDocuments || hasTopics || hasSessions;

        // Skip onboarding redirect - users can access the app directly
      } catch (error) {
        console.error('Error checking user status:', error);
      }
    };
    checkUserStatus();
  }, [user, navigate]);

  // Fetch user documents
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!user) return;
      try {
        console.log('Fetching documents for user:', user.id);
        const {
          data,
          error
        } = await supabase.from('documents').select(`
            id, 
            original_filename,
            upload_timestamp,
            topics (
              title
            )
          `).eq('user_id', user.id).eq('status', 'processed').order('upload_timestamp', {
          ascending: false
        });
        if (error) {
          console.error('Error fetching documents:', error);
          // Try without status filter as fallback
          const {
            data: fallbackData,
            error: fallbackError
          } = await supabase.from('documents').select(`
              id, 
              original_filename, 
              status,
              upload_timestamp,
              topics (
                title
              )
            `).eq('user_id', user.id).order('upload_timestamp', {
            ascending: false
          });
          if (fallbackError) {
            console.error('Fallback fetch also failed:', fallbackError);
            return;
          }
          console.log('Fallback data:', fallbackData);
          // Filter processed documents in JavaScript
          const processedDocs = fallbackData?.filter(doc => doc.status === 'processed') || [];
          const formattedDocs = processedDocs.map(doc => ({
            id: doc.id,
            name: doc.original_filename,
            topicName: doc.topics?.title || null,
            uploadTimestamp: doc.upload_timestamp
          }));
          setDocuments(formattedDocs);

          // Auto-select the first document if there's only one
          if (formattedDocs.length === 1 && !selectedDocument) {
            setSelectedDocument(formattedDocs[0].id);
          }
          return;
        }
        console.log('Documents fetched successfully:', data);
        const formattedDocs = data?.map(doc => ({
          id: doc.id,
          name: doc.original_filename,
          topicName: doc.topics?.title || null,
          uploadTimestamp: doc.upload_timestamp
        })) || [];
        setDocuments(formattedDocs);

        // Auto-select the first document if there's only one
        if (formattedDocs.length === 1 && !selectedDocument) {
          setSelectedDocument(formattedDocs[0].id);
        }
      } catch (error) {
        console.error('Error fetching documents:', error);
      }
    };
    fetchDocuments();
  }, [user]);

  // Handle session creation
  const handleStartSession = async () => {
    if (!user || !selectedDocument || !sessionDescription.trim()) {
      toast({
        title: "Missing Information",
        description: "Please select a document and enter a description.",
        variant: "destructive"
      });
      return;
    }
    try {
      // Create a new session
      const {
        data: sessionData,
        error: sessionError
      } = await supabase.from('sessions').insert({
        user_id: user.id,
        document_id: selectedDocument,
        title: sessionDescription.trim(),
        conversation_history: [],
        total_duration_seconds: 0,
        initial_prompt: sessionDescription.trim() // Store the initial prompt from textarea
      }).select().single();
      if (sessionError) {
        console.error('Error creating session:', sessionError);
        toast({
          title: "Error",
          description: "Failed to create session. Please try again.",
          variant: "destructive"
        });
        return;
      }

      // Navigate to the new session
      navigate(`/session/${sessionData.id}`);
    } catch (error) {
      console.error('Error starting session:', error);
      toast({
        title: "Error",
        description: "Failed to start session. Please try again.",
        variant: "destructive"
      });
    }
  };

  // New streamlined upload flow - create topic and navigate directly to canvas
  const handleUploadDocumentFlow = async () => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please log in to upload documents.",
        variant: "destructive"
      });
      return;
    }
    if (isCreating) {
      toast({
        title: "Please wait",
        description: "Already creating a topic. Please wait.",
        variant: "default"
      });
      return;
    }
    try {
      // Create a new topic
      const newTopic = await createTopic("New Document");
      if (!newTopic) {
        throw new Error("Failed to create topic");
      }

      // Navigate to topic page with parameter to open upload modal
      navigate(`/topic/${newTopic.id}?openUploadModal=true`);
    } catch (error) {
      console.error('Topic creation error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast({
        title: "Failed to Create Topic",
        description: `Failed to create topic: ${errorMessage}`,
        variant: "destructive"
      });
    }
  };

  // New streamlined YouTube flow - create topic and navigate directly to canvas
  const handleYouTubeFlow = async () => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please log in to add YouTube videos.",
        variant: "destructive"
      });
      return;
    }
    if (isCreating) {
      toast({
        title: "Please wait",
        description: "Already creating a topic. Please wait.",
        variant: "default"
      });
      return;
    }
    try {
      // Create a new topic
      const newTopic = await createTopic("New YouTube Video");
      if (!newTopic) {
        throw new Error("Failed to create topic");
      }

      // Navigate to topic page with parameter to open YouTube modal
      navigate(`/topic/${newTopic.id}?openYouTubeModal=true`);
    } catch (error) {
      console.error('Topic creation error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast({
        title: "Failed to Create Topic",
        description: `Failed to create topic: ${errorMessage}`,
        variant: "destructive"
      });
    }
  };

  // Handle study mode selection
  const handleSelectStudyMode = (mode: StudyMode) => {
    setSelectedStudyMode(mode);
    setSessionDescription(mode.prompt);
    setShowTextArea(true);
  };
  return <AppLayout pageTitle="StudiBudi">
      <div className={`h-full bg-app-background flex flex-col min-h-0 overflow-y-auto ${isScrolling ? 'scrollbar-thin-light' : 'scrollbar-hide'}`} onScroll={handleScroll}>
        <div className={`h-full flex ${isTabletOrMobile ? 'flex-col' : 'flex-row'} gap-6 md:gap-8 p-4 md:p-6 lg:p-8 overflow-y-auto pb-6`}>
          {/* Main Content */}
          <div className={`${isTabletOrMobile ? 'w-full' : 'w-full max-w-[760px] mx-auto'} min-w-0 flex items-center justify-center h-full`}>
            {/* Welcome Greeting - Minimal Design */}
            {/* <div className="py-6 flex items-center justify-center">
              <div className="w-full px-3 text-center">
                <img src="/lovable-uploads/efa2905b-bb1d-40a6-8600-de4c01056bcd.png" alt="Waving hand" className="w-[72px] h-[72px] mx-auto mb-4" />
                <h1 className="text-[28px] font-heading font-semibold text-slate-900 mb-1">
                  Hello {userProfile.full_name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Student'}
                </h1>
                <p className="text-[20px] text-slate-500 mb-2">
                  What would you like to study today?
                </p>
                
                <div className="max-w-4xl mx-auto">
                  <div className="flex flex-col md:flex-row gap-4 w-full">
                {activities.map(activity => <div key={activity.id} onClick={() => {
                    if (activity.id === 'upload-document') {
                      handleUploadDocumentFlow();
                    } else if (activity.id === 'youtube-link') {
                      handleYouTubeFlow();
                    }
                  }} className="flex-1 bg-white border border-gray-200 rounded-2xl p-6 cursor-pointer transition-all duration-200 hover:shadow-sm group relative px-[16px] py-[16px] disabled:opacity-50" 
                  style={{ opacity: (activity.id === 'upload-document' && isUploading) || (isCreating) ? 0.5 : 1, pointerEvents: (isUploading || isCreating) ? 'none' : 'auto' }}>
                        <div className="flex items-start space-x-4">
                          <img 
                            src={activity.id === 'youtube-link' ? "/lovable-uploads/001600d1-42c4-4823-a71c-524c7e4bbb17.png" : "/lovable-uploads/be975b46-abd0-43f8-a026-cd410ed25577.png"} 
                            alt={activity.title} 
                            className="w-12 h-12 flex-shrink-0" 
                          />
                          <div className="flex-1">
                            <h3 className="font-medium text-sm text-slate-700 mb-1 text-left">
                              {activity.title}
                            </h3>
                            <p className="text-xs leading-relaxed text-slate-500 text-left">
                              {activity.description}
                            </p>
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>
             </div> */}
            
            {/* <TopicTilesGrid /> */}
            <TopicCreationWidget />
          </div>
          
          {/* Right Sidebar */}
          {/* <div className={`${isTabletOrMobile ? 'w-full mt-4' : 'w-96 flex-shrink-0'} min-w-0`}>
            <div className={`${isTabletOrMobile ? '' : 'sticky top-6'} space-y-4`}>
              <OnboardingChecklist />
              <StreakCard />
              <SubscriptionCard />
            </div>
           </div> */}
        </div>


        {/* YouTube URL Modal - Remove since we now navigate directly */}
      </div>
    </AppLayout>;
};
export default LandingPage;